<template>
    <div class="gestione-export">

        <!-- Toolbar superiore: selettori + azioni -->
        <v-card class="mb-4 elevation-1" rounded="lg">
            <v-card-text class="py-3">
                <v-row align="center" dense>
                    <v-col cols="12" sm="auto">
                        <span class="text-body-2 font-weight-medium text-medium-emphasis">
                            <v-icon size="16" class="me-1">mdi-layers-outline</v-icon>
                            Modulo:
                        </span>
                    </v-col>
                    <v-col cols="6" sm="3">
                        <v-select v-model="moduloSelezionato" :items="moduli" item-title="label" item-value="value"
                            variant="outlined" density="compact" hide-details @update:model-value="caricaConfig">
                            <template #prepend-inner>
                                <v-icon size="16">mdi-layers-outline</v-icon>
                            </template>
                        </v-select>
                    </v-col>

                    <v-col cols="12" sm="auto">
                        <span class="text-body-2 font-weight-medium text-medium-emphasis">
                            <v-icon size="16" class="me-1">mdi-file-export-outline</v-icon>
                            Formato:
                        </span>
                    </v-col>
                    <v-col cols="6" sm="3">
                        <v-select v-model="formatoSelezionato" :items="formati" item-title="label" item-value="value"
                            variant="outlined" density="compact" hide-details @update:model-value="caricaConfig">
                            <template #prepend-inner>
                                <v-icon size="16">{{ iconaFormato }}</v-icon>
                            </template>
                            <template #item="{ props, item }">
                                <v-list-item v-bind="props">
                                    <template #prepend>
                                        <v-icon size="18" class="me-2">{{ item.raw.icona }}</v-icon>
                                    </template>
                                </v-list-item>
                            </template>
                        </v-select>
                    </v-col>

                    <v-col cols="auto" class="ms-auto d-flex gap-2">
                        <!-- Toggle anteprima -->
                        <v-btn size="small" :variant="mostraAnteprima ? 'tonal' : 'outlined'"
                            :color="mostraAnteprima ? 'info' : 'default'" prepend-icon="mdi-eye-outline"
                            @click="mostraAnteprima = !mostraAnteprima" class="mr-2">
                            Anteprima
                        </v-btn>
                        <v-btn size="small" color="primary" prepend-icon="mdi-content-save-outline" :loading="salvando"
                            @click="salvaConfig">
                            Salva modifiche
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Legenda -->
        <v-card class="mb-4 elevation-0 border" rounded="lg">
            <v-card-text class="py-2 px-4">
                <v-row dense align="center">
                    <v-col cols="auto">
                        <span class="text-caption text-medium-emphasis font-weight-medium me-3">
                            Legenda:
                        </span>
                    </v-col>
                    <v-col cols="auto" class="d-flex align-center gap-1">
                        <v-icon size="14" color="grey">mdi-drag-vertical</v-icon>
                        <span class="text-caption">Trascina fogli, sezioni e campi per riordinare</span>
                    </v-col>
                    <v-col cols="auto" class="d-flex align-center gap-1">
                        <v-icon size="14" color="primary">mdi-toggle-switch</v-icon>
                        <span class="text-caption">Toggle = includi/escludi dall'export</span>
                    </v-col>
                    <v-col cols="auto" class="d-flex align-center gap-1">
                        <v-icon size="14" color="warning">mdi-alert-circle-outline</v-icon>
                        <span class="text-caption">
                            Foglio/sezione nascosti escludono tutti i campi figli
                        </span>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Layout: configurazione + anteprima affiancati -->
        <v-row>
            <!-- Colonna configurazione -->
            <v-col :cols="mostraAnteprima ? 7 : 12" class="config-col">
                <template v-if="loading">
                    <v-skeleton-loader v-for="i in 2" :key="i" type="card" class="mb-3" />
                </template>

                <template v-else>
                    <!-- FOGLI: drag & drop tra fogli -->
                    <draggable v-model="configLocale.fogli" item-key="id" handle=".foglio-handle"
                        ghost-class="drag-ghost" chosen-class="drag-chosen" @end="onDragFoglio">
                        <template #item="{ element: foglio }">
                            <v-card class="mb-4 elevation-2" rounded="lg"
                                :class="{ 'foglio-nascosto': !foglio.visibile }">

                                <!-- Header foglio -->
                                <v-card-title class="foglio-header px-4 py-3 d-flex align-center gap-2">
                                    <v-icon class="foglio-handle" size="20" color="grey-lighten-1">
                                        mdi-drag-vertical
                                    </v-icon>
                                    <v-icon :icon="iconaFoglio(foglio.codice)" size="18"
                                        :color="foglio.visibile ? 'primary' : 'grey'" />
                                    <span class="text-body-1 font-weight-bold"
                                        :class="foglio.visibile ? '' : 'text-grey'">
                                        {{ foglio.etichetta }}
                                    </span>
                                    <v-chip size="x-small" variant="tonal" :color="foglio.visibile ? 'primary' : 'grey'"
                                        class="ms-1">
                                        {{ conteggioVisibili(foglio) }}
                                        campi inclusi
                                    </v-chip>
                                    <v-spacer />
                                    <v-switch v-model="foglio.visibile" color="primary" density="compact" hide-details
                                        class="toggle-compact" @update:model-value="onToggleFoglio(foglio)" />
                                </v-card-title>

                                <v-divider />

                                <!-- SEZIONI del foglio: drag & drop -->
                                <div class="sezioni-wrap pa-2">
                                    <draggable v-model="foglio.sezioni" item-key="id" handle=".sezione-handle"
                                        ghost-class="drag-ghost" chosen-class="drag-chosen"
                                        @end="onDragSezione(foglio)">
                                        <template #item="{ element: sezione }">
                                            <v-card class="sezione-card mb-2 elevation-0"
                                                :class="{ 'sezione-nascosta': !sezione.visibile || !foglio.visibile }"
                                                rounded="lg">

                                                <!-- Header sezione -->
                                                <div class="sezione-header px-3 py-2 d-flex align-center gap-2">
                                                    <v-icon class="sezione-handle" size="16" color="grey-lighten-1">
                                                        mdi-drag-vertical
                                                    </v-icon>
                                                    <span class="text-body-2 font-weight-semibold"
                                                        :class="(sezione.visibile && foglio.visibile) ? '' : 'text-grey'">
                                                        {{ sezione.etichetta }}
                                                    </span>
                                                    <v-chip size="x-small" variant="tonal" color="default" class="ms-1">
                                                        {{sezione.campi.filter(c => c.visibile).length}}/{{
                                                            sezione.campi.length }}
                                                    </v-chip>
                                                    <v-spacer />
                                                    <!-- Bottone rapido tutto/niente -->
                                                    <v-btn :color="tuttiCampiVisibili(sezione) ? 'error' : 'primary'"
                                                        variant="text" :disabled="!foglio.visibile" size="x-small"
                                                        @click="toggleTuttiCampi(sezione)">
                                                        {{ tuttiCampiVisibili(sezione) ?
                                                            'Escludi tutti' : 'Includi tutti' }}
                                                    </v-btn>
                                                    <v-switch v-model="sezione.visibile" color="primary"
                                                        density="compact" hide-details class="toggle-compact ms-1"
                                                        :disabled="!foglio.visibile"
                                                        @update:model-value="onToggleSezione(sezione)" />
                                                </div>

                                                <!-- CAMPI della sezione: drag & drop -->
                                                <div class="campi-wrap px-3 pb-2">
                                                    <draggable v-model="sezione.campi" item-key="id"
                                                        handle=".campo-handle" ghost-class="drag-ghost"
                                                        chosen-class="drag-chosen" @end="onDragCampo(sezione)">
                                                        <template #item="{ element: campo }">
                                                            <div class="campo-row"
                                                                :class="{ 'campo-escluso': !campo.visibile || !sezione.visibile || !foglio.visibile }">
                                                                <v-icon class="campo-handle" size="14"
                                                                    color="grey-lighten-2">
                                                                    mdi-drag-vertical
                                                                </v-icon>
                                                                <v-chip :color="coloreTipo(campo.tipoInput)"
                                                                    size="x-small" variant="tonal"
                                                                    class="flex-shrink-0">
                                                                    <v-icon start size="9">
                                                                        {{ iconaTipo(campo.tipoInput) }}
                                                                    </v-icon>
                                                                    {{ campo.tipoInput }}
                                                                </v-chip>
                                                                <span class="text-body-2 campo-label"
                                                                    :class="campo.visibile && sezione.visibile && foglio.visibile ? '' : 'text-grey'">
                                                                    {{ campo.etichetta }}
                                                                </span>
                                                                <span
                                                                    class="text-caption text-medium-emphasis campo-key">
                                                                    {{ campo.campoKey }}
                                                                </span>
                                                                <v-switch v-model="campo.visibile" color="primary"
                                                                    density="compact" hide-details
                                                                    class="toggle-compact ms-auto"
                                                                    :disabled="!sezione.visibile || !foglio.visibile" />
                                                            </div>
                                                        </template>
                                                    </draggable>
                                                </div>

                                            </v-card>
                                        </template>
                                    </draggable>
                                </div>

                            </v-card>
                        </template>
                    </draggable>
                </template>
            </v-col>

            <!-- Colonna anteprima -->
            <v-col v-if="mostraAnteprima" cols="5" class="anteprima-col">
                <div class="anteprima-sticky">
                    <v-card class="elevation-2" rounded="lg">
                        <v-card-title class="anteprima-header px-4 py-3 d-flex align-center">
                            <v-icon :icon="iconaFormato" size="18" class="me-2"
                                :color="formatoSelezionato === 'excel' ? 'success' : 'error'" />
                            <span class="text-body-2 font-weight-bold">
                                Anteprima — {{ configLocale.formatoEtichetta || formatoSelezionato }}
                            </span>
                            <v-chip size="x-small" class="ms-2" variant="tonal" color="info">
                                live
                            </v-chip>
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-3">

                            <!-- Tab dei fogli visibili -->
                            <div v-if="fogliVisibili.length === 0" class="text-center text-caption text-grey pa-4">
                                Nessun foglio/sezione visibile
                            </div>

                            <template v-else>
                                <!-- Simulazione tab stile Excel -->
                                <div class="excel-tabs mb-3">
                                    <div v-for="(f, idx) in fogliVisibili" :key="f.id" class="excel-tab"
                                        :class="{ 'excel-tab--active': tabAnteprima === idx }"
                                        @click="tabAnteprima = idx">
                                        <v-icon size="12" class="me-1">
                                            {{ iconaFoglio(f.codice) }}
                                        </v-icon>
                                        {{ f.etichetta }}
                                    </div>
                                </div>

                                <!-- Contenuto foglio selezionato -->
                                <div v-if="fogliVisibili[tabAnteprima]" class="foglio-preview">
                                    <div v-for="sezione in sezioniVisibiliDiFoglio(fogliVisibili[tabAnteprima])"
                                        :key="sezione.id" class="preview-sezione mb-3">

                                        <!-- Riga intestazione sezione -->
                                        <div class="preview-section-header">
                                            {{ sezione.etichetta.toUpperCase() }}
                                        </div>

                                        <!-- Righe campo: etichetta | valore placeholder -->
                                        <div v-for="campo in campiVisibiliDiSezione(sezione)" :key="campo.id"
                                            class="preview-field-row">
                                            <div class="preview-label">{{ campo.etichetta }}</div>
                                            <div class="preview-value">
                                                {{ placeholderValore(campo.tipoInput) }}
                                            </div>
                                        </div>

                                    </div>
                                </div>

                            </template>

                        </v-card-text>
                    </v-card>
                </div>
            </v-col>
        </v-row>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" :timeout="3000">
            {{ snackbar.text }}
        </v-snackbar>

    </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import draggable from 'vuedraggable'
import { get_config_export_admin, update_config_export_batch } from '@/api/config/api.export.js'

// Formati supportati (estendere quando si aggiunge PDF)
const FORMATI = [
    { label: 'Excel (.xlsx)', value: 'excel', icona: 'mdi-microsoft-excel' },
    /* { label: 'PDF', value: 'pdf', icona: 'mdi-file-pdf-box' }, */
]

const MODULI = [
    { label: 'Serramenti', value: 'serramenti' },
    /* { label: 'Persiane', value: 'persiane' },
    { label: 'Porte Interne', value: 'porte_interne' }, */
]

export default {
    name: 'GestioneExportForm',
    components: { draggable },

    props: {
        aziendaIdEsterno: { type: Number, default: null }
    },

    setup(props) {
        const store = useStore()
        const isSuperAdmin = computed(() => store.getters['user/getRole'] === 3)

        const loading = ref(false)
        const salvando = ref(false)
        const mostraAnteprima = ref(true)
        const tabAnteprima = ref(0)

        const moduloSelezionato = ref('serramenti')
        const formatoSelezionato = ref('excel')

        // Struttura locale modificabile
        const configLocale = ref({ fogli: [], formatoEtichetta: '', formatoIcona: '' })

        const snackbar = ref({ show: false, text: '', color: 'success' })
        const notifica = (text, color = 'success') => { snackbar.value = { show: true, text, color } }

        // ── Caricamento ──────────────────────────────────────────────────────
        const caricaConfig = async () => {
            loading.value = true
            tabAnteprima.value = 0
            try {
                const res = await get_config_export_admin(
                    moduloSelezionato.value, formatoSelezionato.value, props.aziendaIdEsterno)
                // Deep clone per lavorare localmente
                configLocale.value = JSON.parse(JSON.stringify(res.data))
            } catch {
                notifica('Errore nel caricamento della configurazione export', 'error')
            } finally {
                loading.value = false
            }
        }

        // ── Salvataggio ──────────────────────────────────────────────────────
        const salvaConfig = async () => {
            salvando.value = true
            try {
                const items = raccogliItems()
                await update_config_export_batch(
                    moduloSelezionato.value, formatoSelezionato.value, items, props.aziendaIdEsterno)
                notifica('Configurazione export salvata')
            } catch {
                notifica('Errore durante il salvataggio', 'error')
            } finally {
                salvando.value = false
            }
        }

        // Raccoglie tutti i record da aggiornare con ordinamento corrente
        const raccogliItems = () => {
            const items = []
            configLocale.value.fogli.forEach((foglio, fi) => {
                items.push({ id: foglio.id, visibile: foglio.visibile, ordinamento: (fi + 1) * 10 })
                foglio.sezioni.forEach((sezione, si) => {
                    items.push({ id: sezione.id, visibile: sezione.visibile, ordinamento: (si + 1) * 10 })
                    sezione.campi.forEach((campo, ci) => {
                        items.push({ id: campo.id, visibile: campo.visibile, ordinamento: (ci + 1) * 10 })
                    })
                })
            })
            return items
        }

        // ── Toggle helpers ───────────────────────────────────────────────────
        // Quando si nasconde un foglio, propaga ai figli (solo visivo, il DB
        // mantiene lo stato dei singoli campi per poterli ripristinare)
        const onToggleFoglio = (foglio) => {
            if (!foglio.visibile) {
                foglio.sezioni.forEach(s => { s._parentHidden = true })
            } else {
                foglio.sezioni.forEach(s => { s._parentHidden = false })
            }
        }

        const onToggleSezione = (/* sezione */) => {
            // nessuna propagazione automatica ai campi: l'utente
            // può nascondere la sezione tenendo i campi "pronti" per riabilitarla
        }

        const tuttiCampiVisibili = (sezione) => sezione.campi.every(c => c.visibile)
        const toggleTuttiCampi = (sezione) => {
            const nuovoStato = !tuttiCampiVisibili(sezione)
            sezione.campi.forEach(c => { c.visibile = nuovoStato })
        }

        // ── Drag end: aggiorna ordinamento locale ────────────────────────────
        const onDragFoglio = () => {
            configLocale.value.fogli.forEach((f, i) => { f.ordinamento = (i + 1) * 10 })
        }
        const onDragSezione = (foglio) => {
            foglio.sezioni.forEach((s, i) => { s.ordinamento = (i + 1) * 10 })
        }
        const onDragCampo = (sezione) => {
            sezione.campi.forEach((c, i) => { c.ordinamento = (i + 1) * 10 })
        }

        // ── Computed per anteprima ───────────────────────────────────────────
        const fogliVisibili = computed(() =>
            (configLocale.value.fogli || []).filter(f => f.visibile)
        )

        const sezioniVisibiliDiFoglio = (foglio) =>
            (foglio.sezioni || []).filter(s => s.visibile)

        const campiVisibiliDiSezione = (sezione) =>
            (sezione.campi || []).filter(c => c.visibile)

        const conteggioVisibili = (foglio) =>
            (foglio.sezioni || []).reduce((acc, s) =>
                acc + (s.campi || []).filter(c => c.visibile).length, 0)

        // ── Utils display ────────────────────────────────────────────────────
        const iconaFoglio = (codice) => ({
            dati_commessa: 'mdi-clipboard-text-outline',
            misure: 'mdi-ruler-square',
        }[codice] || 'mdi-file-outline')

        const iconaFormato = computed(() =>
            FORMATI.find(f => f.value === formatoSelezionato.value)?.icona || 'mdi-file-export-outline'
        )

        const coloreTipo = (tipo) => ({
            text: 'primary', number: 'secondary', textarea: 'info',
            select: 'warning', checkbox: 'success', date: 'purple'
        }[tipo] || 'grey')

        const iconaTipo = (tipo) => ({
            text: 'mdi-form-textbox', number: 'mdi-numeric',
            textarea: 'mdi-form-textarea', select: 'mdi-chevron-down-box-outline',
            checkbox: 'mdi-checkbox-marked-outline', date: 'mdi-calendar-outline'
        }[tipo] || 'mdi-form-textbox')

        const placeholderValore = (tipo) => {
            if (tipo === 'date') return '01/01/2026'
            if (tipo === 'number') return '120'
            if (tipo === 'checkbox') return 'Sì'
            return 'Valore esempio'
        }

        onMounted(async () => {
            caricaConfig();
            await Promise.all([caricaConfig()]);
        })

        watch(() => props.aziendaIdEsterno, caricaConfig)

        return {
            loading, salvando, mostraAnteprima, tabAnteprima,
            moduli: MODULI, formati: FORMATI,
            moduloSelezionato, formatoSelezionato, iconaFormato,
            configLocale, fogliVisibili,
            caricaConfig, salvaConfig,
            onToggleFoglio, onToggleSezione,
            tuttiCampiVisibili, toggleTuttiCampi,
            onDragFoglio, onDragSezione, onDragCampo,
            sezioniVisibiliDiFoglio, campiVisibiliDiSezione, conteggioVisibili,
            iconaFoglio, coloreTipo, iconaTipo, placeholderValore,
            snackbar, isSuperAdmin
        }
    }
}
</script>

<style scoped>
/* ── Header foglio ── */
.foglio-header {
    background: #f0f4f8;
    border-radius: 12px 12px 0 0;
    min-height: 52px;
}

.foglio-nascosto {
    opacity: .6;
}

.foglio-handle {
    cursor: grab;
}

.foglio-handle:active {
    cursor: grabbing;
}

/* ── Sezione ── */
.sezioni-wrap {
    background: #fafafa;
}

.sezione-card {
    border: 1px solid rgba(0, 0, 0, .07) !important;
    background: white !important;
}

.sezione-header {
    background: #f7f7f8;
    border-radius: 8px 8px 0 0;
    border-bottom: 1px solid rgba(0, 0, 0, .06);
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    padding: 8px 12px;
}

.sezione-nascosta {
    opacity: .5;
}

.sezione-handle {
    cursor: grab;
}

.sezione-handle:active {
    cursor: grabbing;
}

/* ── Campi ── */
.campi-wrap {
    padding-top: 4px;
}

.campo-row {
    display: grid;
    grid-template-columns: 18px 70px 1fr 110px 48px;
    align-items: center;
    gap: 8px;
    padding: 5px 4px;
    border-bottom: 1px solid rgba(0, 0, 0, .04);
    transition: background .12s ease;
    min-height: 36px;
}

.campo-row:last-child {
    border-bottom: none;
}

.campo-row:hover {
    background: #f0f4f8;
    border-radius: 6px;
}

.campo-escluso {
    opacity: .4;
}

.campo-handle {
    cursor: grab;
}

.campo-handle:active {
    cursor: grabbing;
}

.campo-label {
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.campo-key {
    font-family: 'Courier New', monospace;
    font-size: 10px;
    background: rgba(30, 58, 95, .06);
    padding: 1px 5px;
    border-radius: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ── Toggle compatto ── */
.toggle-compact :deep(.v-switch__track) {
    height: 16px;
    width: 32px;
}

.toggle-compact :deep(.v-switch__thumb) {
    width: 12px;
    height: 12px;
}

/* ── Drag ── */
.drag-ghost {
    opacity: .4;
    background: #e0e7ff !important;
}

.drag-chosen {
    background: #eff6ff !important;
    box-shadow: 0 4px 12px rgba(30, 58, 95, .12);
}

/* ── Anteprima sticky ── */
.anteprima-sticky {
    position: sticky;
    top: 16px;
}

.anteprima-header {
    background: #f7f7f8;
    border-radius: 12px 12px 0 0;
    border-bottom: 1px solid rgba(0, 0, 0, .06);
}

/* ── Tab simulazione Excel ── */
.excel-tabs {
    display: flex;
    gap: 2px;
    border-bottom: 2px solid #1E3A5F;
}

.excel-tab {
    padding: 4px 12px;
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    background: #f0f0f0;
    border-radius: 4px 4px 0 0;
    cursor: pointer;
    border: 1px solid #ddd;
    border-bottom: none;
    display: flex;
    align-items: center;
    transition: background .15s;
}

.excel-tab:hover {
    background: #e0e7ff;
    color: #1E3A5F;
}

.excel-tab--active {
    background: white;
    color: #1E3A5F;
    border-color: #1E3A5F;
}

/* ── Contenuto anteprima foglio ── */
.foglio-preview {
    max-height: 560px;
    overflow-y: auto;
}

.preview-sezione {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
}

.preview-section-header {
    background: #1E3A5F;
    color: white;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .6px;
    padding: 4px 10px;
}

.preview-field-row {
    display: grid;
    grid-template-columns: 130px 1fr;
    border-bottom: 1px solid #f1f5f9;
    min-height: 24px;
}

.preview-field-row:last-child {
    border-bottom: none;
}

.preview-label {
    background: #f8fafc;
    font-size: 10px;
    font-weight: 600;
    color: #374151;
    padding: 3px 8px;
    border-right: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
}

.preview-value {
    font-size: 10px;
    color: #9ca3af;
    padding: 3px 8px;
    display: flex;
    align-items: center;
    font-style: italic;
}

/* ── Responsive ── */
@media (max-width: 960px) {
    .campo-row {
        grid-template-columns: 18px 60px 1fr 40px;
    }

    .campo-key {
        display: none;
    }
}
</style>